/**
 * The words under each passkey on the passkeys page. Takes `now` rather than
 * reading the clock, so it is tested without one.
 */
import { format, formatDistanceStrict } from "date-fns";

import type { MFPasskey } from "state/members/passkeys";

const parse = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Added 3 Oct 2026". */
export const addedLabel = (passkey: Pick<MFPasskey, "createdAt">): string => {
  const date = parse(passkey.createdAt);
  return date ? `Added ${format(date, "d MMM yyyy")}` : "Added at an unknown time";
};

/** "Last used 2 days ago", or "Never used". */
export const lastUsedLabel = (
  passkey: Pick<MFPasskey, "lastUsedAt">,
  now: number,
): string => {
  const date = parse(passkey.lastUsedAt);
  if (!date) return "Never used";
  // A use a moment "in the future" is clock skew, not time travel.
  if (date.getTime() >= now) return "Last used just now";
  return `Last used ${formatDistanceStrict(date, new Date(now), { addSuffix: true })}`;
};

/** Where the passkey lives, in the terms a person recognises. */
export const storageLabel = (passkey: Pick<MFPasskey, "backedUp">): string =>
  passkey.backedUp ? "Synced" : "This device only";

export const storageHint = (passkey: Pick<MFPasskey, "backedUp">): string =>
  passkey.backedUp
    ? "Saved in your password manager or cloud keychain, so it works on your other devices too."
    : "Saved on one device or security key. If you lose it, this passkey goes with it.";
